/**
 * Mapeo columna(BD) <-> campo(data del formulario) para Declaración de Renta
 * F-22, Régimen 14 D N°8 (Pro Pyme Transparente), común a los años con los mismos
 * recuadros 22 y 23 (AT 2025 y AT 2026). Cada año lo arma con su número en
 * <año>/14d8/persistence.js, que es lo que leen el backend (schemas/<año>_14d8.js) y
 * el frontend (RentaSavedView, vía el registry).
 *
 * La copia de la DJ 1947 usada se guarda en la columna dj1947, así la renta calculada
 * se ve igual aunque la DJ cambie después.
 */
const formulas = require('./formulas');
const { crearEstadoInicial } = require('./initialState');
const { buildRenta14d8Html } = require('./pdfExport');
const { LINEAS_INGRESOS, LINEAS_EGRESOS } = require('../../../../services/dj1947/calculo');

// Campos de entrada: 1:1 con initialState.js (antecedentes.*)
const INPUT_FIELDS = [
  { column: 'cpt_inicial_positivo', path: 'antecedentes.cptInicialPositivo', type: 'number' },
  { column: 'cpt_inicial_negativo', path: 'antecedentes.cptInicialNegativo', type: 'number' },
  { column: 'aumentos_capital', path: 'antecedentes.aumentosCapital', type: 'number' },
  { column: 'disminuciones_capital', path: 'antecedentes.disminucionesCapital', type: 'number' },
  { column: 'pago_intereses_multas', path: 'antecedentes.pagoInteresesMultas', type: 'number' },
  { column: 'pago_idpc_anterior', path: 'antecedentes.pagoIDPCAnterior', type: 'number' },
  { column: 'otras_partidas_agregar', path: 'antecedentes.otrasPartidasAgregar', type: 'number' },
  { column: 'otras_partidas_deducir', path: 'antecedentes.otrasPartidasDeducir', type: 'number' },
];

// Códigos SII de salida, con las mismas funciones que muestran las secciones en pantalla
const OUTPUT_CODES = [
  // Recuadro 22: las líneas de la DJ 1947 y sus totales
  ...[...LINEAS_INGRESOS, ...LINEAS_EGRESOS].map(({ codigo }) => ({ column: `cod_${codigo}`, fn: (d) => formulas.r22.linea(d, codigo) })),
  { column: 'cod_1610', fn: (d) => formulas.r22.totalIngresos(d) },
  { column: 'cod_1629', fn: (d) => formulas.r22.totalEgresos(d) },
  { column: 'cod_1630', fn: (d) => formulas.r22.baseImponible(d) },
  // Recuadro 23
  { column: 'cod_1580', fn: (d) => formulas.r23.cptInicialPositivo(d) },
  { column: 'cod_1582', fn: (d) => formulas.r23.cptInicialNegativo(d) },
  { column: 'cod_1573', fn: (d) => formulas.r23.capitalAportado(d) },
  { column: 'cod_1574', fn: (d) => formulas.r23.aumentosCapital(d) },
  { column: 'cod_1575', fn: (d) => formulas.r23.disminucionesCapital(d) },
  { column: 'cod_1712', fn: (d) => formulas.r23.baseImponible(d) },
  { column: 'cod_1713', fn: (d) => formulas.r23.perdida(d) },
  { column: 'cod_1714', fn: (d) => formulas.r23.perdidasAnteriores(d) },
  { column: 'cod_1576', fn: (d) => formulas.r23.retiros(d) },
  { column: 'cod_1715', fn: (d) => formulas.r23.ingresoDiferido(d) },
  { column: 'cod_1577', fn: (d) => formulas.r23.gastosNoAceptados(d) },
  { column: 'cod_1716', fn: (d) => formulas.r23.credito33bis(d) },
  { column: 'cod_1578', fn: (d) => formulas.r23.creditoIDPC(d) },
  { column: 'cod_1584', fn: (d) => formulas.r23.otrasAgregar(d) },
  { column: 'cod_1585', fn: (d) => formulas.r23.otrasDeducir(d) },
  { column: 'cod_1581', fn: (d) => formulas.r23.cptsPositivoFinal(d) },
  { column: 'cod_1583', fn: (d) => formulas.r23.cptsNegativoFinal(d) },
  // Liquidación: sin IDPC; CPT del 14 A en cero, como en el F22 de un 14 D N°8
  { column: 'cod_305', fn: (d) => formulas.liquidacion.resultado305(d) },
  { column: 'cod_645', fn: () => 0 },
  { column: 'cod_646', fn: () => 0 },
];

const getPath = (obj, p) => p.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

const setPath = (obj, p, value) => {
  const keys = p.split('.');
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (cur[keys[i]] == null) cur[keys[i]] = {};
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
};

const desdeJson = (valor, vacio) => {
  if (valor == null) return vacio;
  return typeof valor === 'string' ? JSON.parse(valor) : valor;
};

// Arma la fila completa (inputs + copia de la DJ + códigos calculados) a partir del
// `data` del formulario. El cálculo ocurre acá, una sola vez, al hacer "Calcular Renta".
function stateToRow(data) {
  const row = {};
  for (const f of INPUT_FIELDS) row[f.column] = getPath(data, f.path);
  row.socios = JSON.stringify(data.antecedentes.socios || []);
  row.dj1947 = JSON.stringify(data.dj1947 || null);
  for (const c of OUTPUT_CODES) row[c.column] = c.fn(data);
  return row;
}

/**
 * Persistencia de un año tributario
 * @param {number} anioTributario
 */
function crearPersistencia14d8(anioTributario) {
  const createInitialState = () => crearEstadoInicial(anioTributario);

  // Reconstruye el `data` del formulario a partir de una fila guardada. Los valores
  // calculados se vuelven a derivar con recalculate(data).
  const rowToState = (row) => {
    const data = createInitialState();
    for (const f of INPUT_FIELDS) {
      const raw = row[f.column];
      setPath(data, f.path, f.type === 'string' ? (raw ?? '') : Number(raw ?? 0));
    }
    data.antecedentes.socios = desdeJson(row.socios, []);
    data.dj1947 = desdeJson(row.dj1947, null);
    return data;
  };

  return {
    TABLE_PROPUESTO: `renta_${anioTributario}_14d8`,
    // El F-22 presentado ante el SII (certificado de renta pagada)
    TABLE_PAGADO: `renta_${anioTributario}_14d8_pagado`,
    INPUT_FIELDS,
    OUTPUT_CODES,
    createInitialState,
    stateToRow,
    rowToState,
    recalculate: formulas.recalculate,
    validar: formulas.validar,
    buildPdfHtml: buildRenta14d8Html,
  };
}

module.exports = { crearPersistencia14d8 };
